import Decimal from "decimal.js";
import { YES } from "../constants/yes_no";
import { PRIMARY, SECONDARY } from "../constants/property_disposition";

// Marks the single Invest REO entry LANIS manages automatically for a current
// home Borrower 1 is keeping as a rental or a second home. (Defined here, with
// no calculation imports, so the reserve and DTI math can both use it without a
// circular import; retained_residence_sync re-exports it.)
export const AUTO_RETAINED_REO_TAG = "auto_from_retained_residence";

export function normalized_address(address) {
  return String(address || "")
    .trim()
    .toLowerCase()
    .replace(/[.,#]/g, "")
    .replace(/\s+/g, " ");
}

/**
 * The Invest REO rows that count - each property ONCE.
 *  - The refi subject row is skipped (the Subject Property covers it).
 *  - When the same address appears more than once (e.g. the current home
 *    imported as an owner-occupied REO row AND auto-added by LANIS as a kept
 *    rental / second home), only one row counts, and the LANIS row wins.
 * The LO never has to uncheck a duplicate; LANIS does it.
 */
export function counted_reo_properties(investment_properties) {
  const rows = (investment_properties || []).filter(
    p => p && p.is_subject_property !== YES
  );
  const keptAddresses = new Set(
    rows
      .filter(p => p[AUTO_RETAINED_REO_TAG] === YES)
      .map(p => normalized_address(p.property_address))
  );
  const seen = new Set();
  return rows.filter(p => {
    const addr = normalized_address(p.property_address);
    if (!addr) return true; // no address: can't match, keep it
    if (keptAddresses.has(addr) && p[AUTO_RETAINED_REO_TAG] !== YES) {
      return false;
    }
    if (seen.has(addr)) return false;
    seen.add(addr);
    return true;
  });
}

function reo_has_mortgage_payment(p) {
  return new Decimal(p.first_mortgage || 0).add(p.second_mortgage || 0).gt(0);
}

/**
 * Addresses of counted Invest REO rows that carry the home's mortgage payment
 * themselves, so a Debt page mortgage attached to that home must NOT be counted
 * in the DTI a second time:
 *  - a rental: its payment is inside the net rental income / loss;
 *  - a second home: its full monthly cost is added to the DTI from Invest REO.
 * A row with no mortgage payment entered is left out, so the Debt page payment
 * is never dropped when nothing else counts it.
 */
function reo_handled_addresses(investment_properties) {
  const handled = new Set();
  counted_reo_properties(investment_properties).forEach(p => {
    if (p.intended_occupancy === PRIMARY) return;
    if (!reo_has_mortgage_payment(p)) return;
    const addr = normalized_address(p.property_address);
    if (addr) handled.add(addr);
  });
  return handled;
}

function attached_key_address(key, investment_properties) {
  const m = /^(res|reo):(\d+)$/.exec(String(key || ""));
  if (!m) return "";
  const index = parseInt(m[2], 10);
  if (m[1] === "reo") {
    const p = (investment_properties || [])[index];
    return p ? normalized_address(p.property_address) : "";
  }
  // "res:0" is Borrower 1's current home - the home LANIS auto-adds to Invest
  // REO when it is kept, so it resolves to that row's address.
  if (index === 0) {
    const kept = (investment_properties || []).find(
      p => p && p[AUTO_RETAINED_REO_TAG] === YES
    );
    return kept ? normalized_address(kept.property_address) : "";
  }
  return "";
}

/**
 * True when this Debt page line is a mortgage attached to a home whose payment
 * Invest REO already counts (see reo_handled_addresses).
 */
export function debt_counted_in_invest_reo(
  accountSummary,
  investment_properties,
  residence
) {
  if (!accountSummary) return false;
  const handled = reo_handled_addresses(investment_properties);
  if (!handled.size) return false;
  // Matched by the "attach to property" checkbox...
  const keys = accountSummary.attached_properties || [];
  const attachedMatch = keys.some(k => {
    const addr = attached_key_address(k, investment_properties);
    return !!addr && handled.has(addr);
  });
  if (attachedMatch) return true;
  // ...or, for a Primary Residence / Second Home line, by the address typed in
  // its Debt page residence box (older loans that were never attached).
  const resAddr = normalized_address(residence && residence.address);
  return !!resAddr && handled.has(resAddr);
}

/**
 * Second homes on Invest REO: the borrower gets no rent, so the FULL monthly
 * cost (mortgage payment + taxes, insurance, HOA, Mello Roos) goes into the DTI.
 */
export function second_home_reo_monthly_total(investment_properties, total_payment) {
  return counted_reo_properties(investment_properties)
    .filter(p => p.intended_occupancy === SECONDARY)
    .reduce((sum, p) => sum.add(total_payment(p)), new Decimal(0));
}
